import type {
  Experience,
  QuestionReflectionPayload,
  QuestionVoice,
  ModerationReport,
  Submission,
  SubmissionPayload,
  SubmissionStatus,
} from "./types";

const API_URL = (import.meta.env.VITE_API_URL || "").replace(/\/$/, "");
const sentEvents = new Set<string>();

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  if (!API_URL) throw new Error("API_NOT_CONFIGURED");
  const response = await fetch(`${API_URL}${path}`, {
    ...init,
    headers: { "Content-Type": "application/json", ...init?.headers },
  });
  if (!response.ok) {
    const result = await response.json().catch(() => ({}));
    throw new Error(result.message || "REQUEST_FAILED");
  }
  return response.json() as Promise<T>;
}

export const publicApi = {
  experiences: (slug: string) =>
    request<{ items: Experience[] }>(`/conversations/${slug}/experiences`),
  submit: (slug: string, payload: SubmissionPayload) =>
    request<{ id: string }>(`/conversations/${slug}/submissions`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  questionReflections: (slug: string) =>
    request<{ items: QuestionVoice[]; count: number }>(
      "/questions/" + slug + "/reflections",
    ),
  submitQuestionReflection: (
    slug: string,
    payload: QuestionReflectionPayload,
  ) =>
    request<{ id: string }>("/questions/" + slug + "/reflections", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  reportQuestionReflection: (
    slug: string,
    reflectionId: string,
    reason: "privacy" | "harm" | "other",
  ) =>
    request<{ accepted: boolean }>(
      "/questions/" + slug + "/reflections/" + reflectionId + "/reports",
      {
        method: "POST",
        body: JSON.stringify({ reason }),
      },
    ),
  resonate: (slug: string) =>
    request<{ count: number }>(`/conversations/${slug}/reactions`, {
      method: "POST",
      body: JSON.stringify({ type: "resonated" }),
    }),
  setReflectionHeart: (
    slug: string,
    action: "like" | "unlike",
    operationId: string,
  ) =>
    request<{ action: "like" | "unlike"; count: number }>(
      `/reflections/${slug}/reactions`,
      {
        method: "POST",
        body: JSON.stringify({ action, operationId }),
      },
    ),
  reflectionHeartCount: (slug: string) =>
    request<{ count: number }>(`/reflections/${slug}/reactions`),
  subscribe: (payload: {
    email: string;
    language: "en" | "ta";
    consent: boolean;
    website?: string;
  }) =>
    request<{ deliveryConfigured: boolean }>("/subscriptions", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  track: (name: string, path: string) => {
    if (!API_URL) return;
    void request<{ accepted: boolean }>("/events", {
      method: "POST",
      body: JSON.stringify({ name, path }),
    }).catch(() => undefined);
  },
  trackOnce: (name: string, path: string) => {
    const key = `${name}:${path}`;
    if (sentEvents.has(key)) return;
    sentEvents.add(key);
    publicApi.track(name, path);
  },
};

const authHeader = (token: string) => ({ Authorization: `Bearer ${token}` });
export const adminApi = {
  list: (token: string, status: SubmissionStatus) =>
    request<{ items: Submission[] }>(`/admin/submissions?status=${status}`, {
      headers: authHeader(token),
    }),
  get: (token: string, id: string) =>
    request<{ item: Submission }>(`/admin/submissions/${id}`, {
      headers: authHeader(token),
    }),
  update: (token: string, id: string, body: Partial<Submission>) =>
    request<{ item: Submission }>(`/admin/submissions/${id}`, {
      method: "PATCH",
      headers: authHeader(token),
      body: JSON.stringify(body),
    }),
  publish: (token: string, id: string) =>
    request<{ item: Submission }>(`/admin/submissions/${id}/publish`, {
      method: "POST",
      headers: authHeader(token),
    }),
  unpublish: (token: string, id: string) =>
    request<{ item: Submission }>(`/admin/submissions/${id}/unpublish`, {
      method: "POST",
      headers: authHeader(token),
    }),
  insights: (token: string) =>
    request<{ totals: Record<string, number>; reflectionHeartTotal: number }>(
      "/admin/insights",
      {
        headers: authHeader(token),
      },
    ),
  reports: (token: string) =>
    request<{ items: ModerationReport[] }>("/admin/reports", {
      headers: authHeader(token),
    }),
  resolveReport: (token: string, id: string) =>
    request<{ item: ModerationReport }>("/admin/reports/" + id, {
      method: "PATCH",
      headers: authHeader(token),
      body: JSON.stringify({ status: "resolved" }),
    }),
};
